import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  UserPlus, 
  Trophy, 
  Calendar, 
  Play, 
  Clock,
  Plus,
  TrendingUp,
  Target,
  Award,
  Zap,
  Shield,
  Eye,
  ArrowRight
} from 'lucide-react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import DataInitializer from '../components/DataInitializer';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isScoreKeeper } = useAuth();
  const [upcomingFixtures, setUpcomingFixtures] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDataInitializer, setShowDataInitializer] = useState(false);
  const [playerCount, setPlayerCount] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [fixtureCount, setFixtureCount] = useState(0);
  const [leagueCount, setLeagueCount] = useState(0);

  // Redirect scorekeepers to fixtures page
  useEffect(() => {
    if (isScoreKeeper()) {
      navigate('/fixtures');
    }
  }, [isScoreKeeper, navigate]);

  useEffect(() => {
    // Listen to upcoming fixtures
    const fixturesQuery = query(
      collection(db, 'fixtures'),
      orderBy('date', 'asc'),
      limit(5)
    );

    const unsubscribeFixtures = onSnapshot(fixturesQuery, (snapshot) => {
      const fixtures = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUpcomingFixtures(fixtures);
      setFixtureCount(snapshot.docs.length);
      setLoading(false);
    });

    // Listen to live matches (check fixtures with status 'live' or 'paused', exclude completed)
    const liveQuery = query(collection(db, 'fixtures'));
    const unsubscribeLive = onSnapshot(liveQuery, (snapshot) => {
      const allFixtures = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const live = allFixtures.filter(f => 
        (f.status === 'live' || f.status === 'paused') && 
        f.status !== 'completed'
      );
      setLiveMatches(live);
    });

    // Listen to players count
    const playersQuery = query(collection(db, 'players'));
    const unsubscribePlayers = onSnapshot(playersQuery, (snapshot) => {
      setPlayerCount(snapshot.docs.length);
    });

    // Listen to teams count
    const teamsQuery = query(collection(db, 'teams'));
    const unsubscribeTeams = onSnapshot(teamsQuery, (snapshot) => {
      setTeamCount(snapshot.docs.length);
      const teamsData = {};
      snapshot.docs.forEach(doc => {
        teamsData[doc.id] = doc.data();
      });
      setTeams(teamsData);
    });

    // Listen to leagues count
    const leaguesQuery = query(collection(db, 'leagues'));
    const unsubscribeLeagues = onSnapshot(leaguesQuery, (snapshot) => {
      setLeagueCount(snapshot.docs.length);
    });

    return () => {
      unsubscribeFixtures();
      unsubscribeLive();
      unsubscribePlayers();
      unsubscribeTeams();
      unsubscribeLeagues();
    };
  }, []);

  // Show data initializer only if no data exists at all
  useEffect(() => {
    if (!loading && playerCount === 0 && teamCount === 0) {
      setShowDataInitializer(true);
    } else {
      setShowDataInitializer(false);
    }
  }, [loading, playerCount, teamCount]);

  const quickActions = [
    {
      title: 'Create Team',
      description: 'Add a new team',
      icon: Shield,
      link: '/teams/create',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Add Player',
      description: 'Register player',
      icon: UserPlus,
      link: '/players/create',
      gradient: 'from-green-500 to-green-600'
    },
    {
      title: 'Create League',
      description: 'New season',
      icon: Trophy,
      link: '/league/create',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      title: 'Generate Fixtures',
      description: 'Auto-generate',
      icon: Calendar,
      link: '/fixtures/generate',
      gradient: 'from-purple-500 to-purple-600'
    }
  ];

  if (showDataInitializer) {
    return (
      <div className="container mx-auto px-4 py-6">
        <DataInitializer onComplete={() => setShowDataInitializer(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      <div className="container mx-auto responsive-padding">
        {/* Hero Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl responsive-padding text-white shadow-xl"
        >
          <div className="responsive-flex items-center justify-between">
            <div>
              <h1 className="responsive-text-4xl font-bold mb-2">
                Welcome Back! 🏆
              </h1>
              <p className="responsive-text-lg text-green-100">
                OffCourt Official League Management Dashboard
              </p>
            </div>
            <Link
              to="/fan"
              target="_blank"
              className="btn bg-white text-green-600 hover:bg-green-50 flex items-center gap-2 shadow-lg"
            >
              <Eye size={20} />
              Fan View
          </Link>
          </div>
        </motion.div>

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="responsive-grid mb-8"
        >
          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="card-elevated bg-gradient-to-br from-green-50 to-white"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
                <Shield size={28} className="text-white" />
              </div>
              <TrendingUp size={20} className="text-green-500" />
            </div>
            <div className="text-3xl font-bold text-green-600 mb-1">{teamCount}</div>
            <div className="text-sm text-gray-600">Active Teams</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="card-elevated bg-gradient-to-br from-blue-50 to-white"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Users size={28} className="text-white" />
              </div>
              <TrendingUp size={20} className="text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-1">{playerCount}</div>
            <div className="text-sm text-gray-600">Players</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="card-elevated bg-gradient-to-br from-purple-50 to-white"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
                <Trophy size={28} className="text-white" />
              </div>
              <TrendingUp size={20} className="text-purple-500" />
            </div>
            <div className="text-3xl font-bold text-purple-600 mb-1">{leagueCount}</div>
            <div className="text-sm text-gray-600">Active Leagues</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -4 }}
            className="card-elevated bg-gradient-to-br from-yellow-50 to-white"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg">
                <Calendar size={28} className="text-white" />
              </div>
              <TrendingUp size={20} className="text-yellow-500" />
            </div>
            <div className="text-3xl font-bold text-yellow-600 mb-1">{upcomingFixtures.length}</div>
            <div className="text-sm text-gray-600">Fixtures</div>
          </motion.div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold mb-4 text-gray-800 flex items-center gap-2">
            <Zap size={24} className="text-green-600" />
            Quick Actions
          </h2>
          <div className="responsive-grid">
            {quickActions.map((action, index) => (
                    <motion.div
                key={action.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.05 }}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to={action.link} className="block">
                  <div className={`card-elevated bg-gradient-to-br ${action.gradient} text-white cursor-pointer`}>
                    <div className="flex items-center justify-between mb-3">
                      <action.icon size={32} />
                      <ArrowRight size={20} className="opacity-70" />
                    </div>
                    <h3 className="font-bold text-lg mb-1">{action.title}</h3>
                    <p className="text-sm opacity-90">{action.description}</p>
                      </div>
                </Link>
                    </motion.div>
                  ))}
                </div>
        </motion.div>

        {/* Live Matches & Upcoming Fixtures */}
        <div className="responsive-flex mb-8">
          {/* Live Matches */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="card-elevated"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Play size={24} className="text-red-500" />
                Live Matches
              </h2>
              {liveMatches.length > 0 && (
                <div className="live-badge text-sm">
                  <div className="live-dot"></div>
                  {liveMatches.length} LIVE
                </div>
              )}
          </div>

            <AnimatePresence>
              {liveMatches.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {liveMatches.slice(0, 3).map((match, idx) => {
                    const homeTeam = teams[match.homeTeamId];
                    const awayTeam = teams[match.awayTeamId];
                    const score = match.sets ? {
                      home: match.sets.filter(s => s.completed && s.homeScore > s.awayScore).length,
                      away: match.sets.filter(s => s.completed && s.awayScore > s.homeScore).length
                    } : { home: 0, away: 0 };

                    return (
                    <motion.div
                      key={match.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: idx * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        style={{
                          padding: '1rem',
                          background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
                          border: '2px solid #FCA5A5',
                          borderRadius: '0.75rem',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                        }}
                      >
                        {/* Teams Side by Side */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
                          {/* Home Team */}
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {homeTeam?.logo ? (
                              <img 
                                src={homeTeam.logo} 
                                alt={match.homeTeam} 
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                  border: '2px solid white'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #D1FAE5 0%, #10B981 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                border: '2px solid white'
                              }}>
                                <Shield size={24} style={{ color: '#059669' }} />
                              </div>
                            )}
                            <span style={{ fontWeight: '700', color: '#1F2937', fontSize: '1rem' }}>{match.homeTeam}</span>
                          </div>

                          {/* Score */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '100px', justifyContent: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#EF4444' }}>
                              {score.home}
                            </div>
                            <div style={{ fontSize: '1.25rem', color: '#9CA3AF' }}>-</div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3B82F6' }}>
                              {score.away}
                            </div>
                          </div>

                          {/* Away Team */}
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem', flexDirection: 'row-reverse' }}>
                            {awayTeam?.logo ? (
                              <img 
                                src={awayTeam.logo} 
                                alt={match.awayTeam} 
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                  border: '2px solid white'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #DBEAFE 0%, #3B82F6 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                border: '2px solid white'
                              }}>
                                <Shield size={24} style={{ color: '#2563EB' }} />
                              </div>
                            )}
                            <span style={{ fontWeight: '700', color: '#1F2937', fontSize: '1rem', textAlign: 'right' }}>{match.awayTeam}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Link
                            to={`/match/${match.id}/scoring`}
                            className="btn btn-sm btn-primary"
                            style={{ flex: 1, textAlign: 'center', fontSize: '0.75rem', textDecoration: 'none' }}
                          >
                            Live Scoring
                          </Link>
                        <Link 
                            to={`/fan/match/${match.id}`}
                            target="_blank"
                            className="btn btn-sm btn-secondary"
                            style={{ flex: 1, textAlign: 'center', fontSize: '0.75rem', textDecoration: 'none' }}
                          >
                            Fan View
                        </Link>
                      </div>
                    </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <Play size={64} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No live matches</p>
                  <p className="text-sm">Start a match to see it here</p>
                </div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Upcoming Fixtures */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="card-elevated"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Calendar size={24} className="text-green-600" />
                Upcoming Fixtures
              </h2>
              <Link to="/fixtures" className="text-sm text-green-600 hover:text-green-700 font-medium">
                View All →
              </Link>
        </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              </div>
            ) : upcomingFixtures.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {upcomingFixtures.map((fixture, idx) => {
                  const homeTeam = teams[fixture.homeTeamId];
                  const awayTeam = teams[fixture.awayTeamId];

                  return (
                    <motion.div
                      key={fixture.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      style={{
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #F0FDF4 0%, #DBEAFE 100%)',
                        borderRadius: '0.75rem',
                        border: '1px solid #E5E7EB'
                      }}
                    >
                      {/* Teams Side by Side */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {homeTeam?.logo ? (
                            <img 
                              src={homeTeam.logo} 
                              alt={fixture.homeTeam} 
                              style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
                            />
                          ) : (
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #D1FAE5 0%, #10B981 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Shield size={20} style={{ color: '#059669' }} />
                            </div>
                          )}
                          <span style={{ fontWeight: '700', fontSize: '0.875rem', color: '#1F2937' }}>{fixture.homeTeam}</span>
                        </div>
                        
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          color: '#9CA3AF',
                          padding: '0.25rem 0.75rem',
                          background: 'white',
                          borderRadius: '9999px'
                        }}>VS</span>
                        
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem', flexDirection: 'row-reverse' }}>
                          {awayTeam?.logo ? (
                            <img 
                              src={awayTeam.logo} 
                              alt={fixture.awayTeam} 
                              style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
                            />
                          ) : (
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #DBEAFE 0%, #3B82F6 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Shield size={20} style={{ color: '#2563EB' }} />
                            </div>
                          )}
                          <span style={{ fontWeight: '700', fontSize: '0.875rem', color: '#1F2937', textAlign: 'right' }}>{fixture.awayTeam}</span>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', fontSize: '0.75rem', color: '#6B7280' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Clock size={12} />
                          <span>{new Date(fixture.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} • {fixture.time}</span>
                        </div>
                        <span>{fixture.court || 'Court TBD'}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Calendar size={64} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No upcoming fixtures</p>
                <p className="text-sm">Generate fixtures to get started</p>
          </div>
        )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
